/**
 * ChokeSkillEffect — Fire tree Choke Fork: dense choking cloud at range.
 *
 * Instantaneously drops a choke_cloud zone at medium range. The cloud deals
 * `choking` DoT (not `burning` — preserves Cauterize's synergy gate), slows
 * enemies by 25%, and crushes detection radius to ~1 tile (87.5% reduction).
 *
 * **Asphyxia** (`choke_asphyxia`): +2s cloud duration, +1 damage/s, and a
 * `blurred_vision` debuff applied via `onEntityExit` when enemies leave.
 *
 * **Blackout** (`choke_density`): +1 tile radius (2 → 3) and backup
 * suppression flag on the zone. The AI alert check lives in the enemy AI
 * dispatch logic, not here — this skill just sets the zone config flag.
 *
 * **Smothered** (`choke_passive`): tree-wide passive that adds duration to
 * smoke_cloud zones; does NOT extend choke_cloud zones. However, Choke itself
 * reads `smokeCloudDurationBonus` as a matter of consistency — the spec says
 * Smothered extends "all smoke clouds," and choke_cloud is not a smoke_cloud.
 * This is a no-op for choke_cloud by design.
 *
 * Long cooldown tier. Breaks Hide on activation.
 *
 * @see fire-spec.md § Choke (`choke`)
 * @see ADR-064 (shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Zone config (subset of ZoneConfig that Choke produces)
// ---------------------------------------------------------------------------

export interface ChokeZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly radiusTiles: number;
  readonly durationMs: number;
  readonly dotDefId: string;
  readonly dotIntensity: number;
  readonly tickIntervalMs: number;
  readonly speedMultiplier: number;
  readonly detectionReduction: number;
  readonly sourceSkillId: string;
  /** When true, enemies inside cannot dispatch alert signals. */
  readonly suppressesBackup?: boolean;
  /** Callback fired when an entity exits the zone (Asphyxia blurred vision). */
  readonly onEntityExit?: (entityId: string, zone: { config: ChokeZoneConfig }) => void;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ChokeDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  /** Returns true when the activation should fizzle (low XP / bad luck). */
  readonly shouldFizzle: () => boolean;
  /** Returns the targeted placement position (medium range from player). */
  readonly getTargetPosition: () => { x: number; y: number };
  readonly breakHide: () => void;
  readonly zones: {
    create(config: ChokeZoneConfig): string;
  };
  /**
   * Apply a status effect to an entity (used for blurred_vision on exit).
   * Only called when Asphyxia is unlocked.
   */
  readonly applyEnemyStatus: (
    entityId: string,
    statusId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ChokeConfig {
  readonly cooldownMs: number;
  readonly radiusTiles: number;
  readonly baseDurationMs: number;
  readonly dotIntensity: number;
  readonly tickIntervalMs: number;
  readonly speedMultiplier: number;
  readonly detectionReduction: number;
}

const DEFAULT_CONFIG: ChokeConfig = {
  cooldownMs: 2000,                // long cooldown tier
  radiusTiles: 2,
  baseDurationMs: 5000,
  dotIntensity: 2,                 // 2 damage/second
  tickIntervalMs: 1000,
  speedMultiplier: 0.75,           // 25% speed reduction
  detectionReduction: 0.875,       // 8 tiles × 0.125 = 1 tile remaining
};

// ---------------------------------------------------------------------------
// Resolver keys
// ---------------------------------------------------------------------------

const KEY_DURATION_MS = 'durationMs';
const KEY_DOT_DAMAGE_PER_SECOND = 'dotDamagePerSecond';
const KEY_AFTER_EFFECT_DURATION_MS = 'afterEffectDurationMs';
const KEY_RADIUS_TILES = 'radiusTiles';
const KEY_SUPPRESSES_BACKUP = 'suppressesBackup';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ChokeSkillEffect implements SkillEffect {
  readonly skillId = 'choke';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: ChokeDeps;
  private readonly config: ChokeConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzleCount = 0;

  constructor(deps: ChokeDeps, config: Partial<ChokeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzleCount(): number { return this._fizzleCount; }

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — on failure, burn the cooldown and bail before side effects
    if (this.deps.shouldFizzle()) {
      this._fizzleCount++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    this.deps.breakHide();

    // --- Resolve upgrade effects ---

    // Asphyxia: +durationMs
    const bonusDuration = this.deps.resolver.getAdditiveEffect('choke', KEY_DURATION_MS);
    const resolvedDuration = this.config.baseDurationMs + bonusDuration;

    // Asphyxia: +dotDamagePerSecond
    const bonusDot = this.deps.resolver.getAdditiveEffect('choke', KEY_DOT_DAMAGE_PER_SECOND);
    const resolvedIntensity = this.config.dotIntensity + bonusDot;

    // Blackout: +radiusTiles
    const bonusRadius = this.deps.resolver.getAdditiveEffect('choke', KEY_RADIUS_TILES);
    const resolvedRadius = this.config.radiusTiles + bonusRadius;

    // Blackout: suppressesBackup
    const suppressesBackup = this.deps.resolver.hasEffect('choke', KEY_SUPPRESSES_BACKUP);

    // Asphyxia: blurred_vision on exit
    const afterEffectMs = this.deps.resolver.getAdditiveEffect('choke', KEY_AFTER_EFFECT_DURATION_MS);
    const hasAfterEffect = afterEffectMs > 0;

    const onEntityExit = hasAfterEffect
      ? (entityId: string, _zone: { config: ChokeZoneConfig }) => {
          this.deps.applyEnemyStatus(entityId, 'blurred_vision', {
            durationMs: afterEffectMs,
            sourceSkillId: 'choke_asphyxia',
          });
        }
      : undefined;

    // --- Create zone ---

    const position = this.deps.getTargetPosition();

    const zoneConfig: ChokeZoneConfig = {
      id: `choke-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'choke_cloud',
      position,
      shape: 'circle',
      radiusTiles: resolvedRadius,
      durationMs: resolvedDuration,
      dotDefId: 'choking',
      dotIntensity: resolvedIntensity,
      tickIntervalMs: this.config.tickIntervalMs,
      speedMultiplier: this.config.speedMultiplier,
      detectionReduction: this.config.detectionReduction,
      sourceSkillId: 'choke',
      ...(suppressesBackup && { suppressesBackup: true }),
      ...(onEntityExit && { onEntityExit }),
    };

    this.deps.zones.create(zoneConfig);

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
